import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import useDelete from "../../../global/hooks/useDelete";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  UnorderedListOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  GiftFilled,
} from "@ant-design/icons";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import FormSection from "../../../global/components/FormSection";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { FaDollarSign, FaGlobe } from "react-icons/fa6";
import AccountCreationModal from "../../../global/components/AccountCreationModal";
import OemIcon from "../../../assets/images/vectors/SideBarIcons/OemIcon";
import DealerIcon from "../../../assets/images/vectors/SideBarIcons/DealerIcon";
import AccountIcon from "../../../assets/images/vectors/SideBarIcons/AccountIcon";
import { hasRole } from "../../../global/hooks/getUserRole";
import { macValidator } from "../../../global/utils/macValidator";
import dayjs from "dayjs";
import { getPurchaseRowId } from "../../Purchase/utils/listSearchStorage";
import {
  applyExpiredToOptions,
  buildLicenseOptionsState,
  getCreatedId,
  modelIconMap,
  orderTypeOptions,
  productIconMap,
  resolveLicenseTypeFromSiteType,
  siteTypeIconMap,
  toDisplayDateTime,
  toVisibleOptionList,
} from "../../../utils/Common";
import DoorTypeIcon from "../../../assets/images/vectors/DoorTypeIcon";
import OptionCards from "../../../global/components/inputWidgets/OptionCards";
import { DeviceLogsOrdersPanel } from "../../Device/Components/DeviceLogsOrdersPanel";
import { getUserRoles } from "../../../global/components/PrivateRoute";
import { useAuthData } from "../../../providers/AuthProvider";

const SITE_EDIT_ROLES = [1, 2, 4, 5, 7, 8];

const CUSTOMER_INFO_FIELDS = [
  { label: "First Name", name: "FirstName" },
  { label: "Last Name", name: "LastName" },
  { label: "Installing Company", name: "Company" },
  { label: "Email", name: "Email" },
  { label: "Phone", name: "Phone" },
  { label: "Address", name: "Address2" },
  { label: "Install Type", name: "InstallType" },
  { label: "Suggestion", name: "Suggestion" },
];

const STATUS_FIELDS = [
  { label: "Proxy Server", name: "ProxyServer" },
  { label: "Secure Port", name: "SecurePort" },
  { label: "Debug Port", name: "DebugPort" },
  { label: "Version", name: "Version" },
  { label: "Address", name: "Address" },
  { label: "HTTPS", name: "Https" },
  { label: "Timezone", name: "Timezone" },
  { label: "Online", name: "Online" },
];

const HALF = { xs: 24, sm: 24, md: 12, lg: 12, xl: 12 };

const toText = (value) => (value ? String(value) : "");
const isBlank = (value) => value == null || value === "";

const licenseTypeForSiteType = (siteType) =>
  siteType == null
    ? undefined
    : resolveLicenseTypeFromSiteType(siteType, orderTypeOptions);

const withIcons = (list, iconMap) =>
  list.map((item) => ({
    ...item,
    icon: iconMap[item.value],
    searchLabel: item.label,
  }));

const renderReadOnlyFields = (fields) =>
  fields.map(({ label, name }) => (
    <Col key={name} {...HALF}>
      <CustomInput label={label} name={name} disabled />
    </Col>
  ));

/** Refetches an option list once when the selected value isn't in it (e.g. just created). */
const useRefetchWhenMissing = (selected, options, ready, refetch) => {
  const lastMissingKey = useRef(null);
  useEffect(() => {
    if (selected == null || !ready) return;
    if (options.some((o) => Number(o.value) === Number(selected))) {
      lastMissingKey.current = null;
      return;
    }
    const key = String(selected);
    if (lastMissingKey.current === key) return;
    lastMissingKey.current = key;
    refetch();
  }, [selected, options, ready, refetch]);
};

export const MySiteForm = ({ type }) => {
  const { userData } = useAuthData();
  const { isSystemUser, isDealerUser } = getUserRoles(userData);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [showPurchaseLicenseConfirm, setShowPurchaseLicenseConfirm] =
    useState(false);
  const pendingPurchaseLicenseRef = useRef(null);
  const [defaultData, setDefaultData] = useState({
    DealerNo: null,
    OemNo: null,
    CustomerNo: null,
  });
  const [OemNo, setOemNo] = useState(null);
  const [dealerNo, setDealerNo] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [sitetype, setSiteType] = useState(0);
  const [licenseType, setLicenseType] = useState(0);
  const [licenseOptionsData, setLicenseOptionsData] = useState({});
  const apiUrl = process.env.REACT_APP_API_URL;
  const siteUrl = `${apiUrl}/api/sites/${id}/`;

  const { data, loading, refetch } = useGet(id ? siteUrl : undefined);
  const { data: licenseOptionData } = useGet(`${apiUrl}/api/licenses/options/`);
  const { data: codesData, loading: codesDataLoading } = useGet(
    `${apiUrl}/api/licensecodes/codes/?limit=100`,
  );
  const { data: optionsData, refetch: optionDataRefetch } = useGet(
    `${apiUrl}/api/devices/options/`,
  );

  const selectedOemNo = OemNo || defaultData?.OemNo || data?.data?.OemNo;
  const {
    data: dealerOptions,
    loading: dealerOptionsLoading,
    refetch: dealerOptionsRefetch,
  } = useGet(
    selectedOemNo
      ? `${apiUrl}/api/dealers/oems/${selectedOemNo}/dealers/`
      : null,
  );

  const selectedDealerNo =
    dealerNo ||
    defaultData?.DealerNo ||
    data?.data?.DealerNo ||
    (hasRole(7, 8, 9) ? userData?.dealer_no : null);
  const {
    data: customerOptions,
    loading: customerOptionsLoading,
    refetch: customerOptionsRefetch,
  } = useGet(
    selectedDealerNo
      ? `${apiUrl}/api/dealers/${selectedDealerNo}/customers/`
      : null,
  );

  const {
    data: postData,
    error: postError,
    postData: postAction,
    loading: postLoading,
  } = usePost(`${apiUrl}/api/sites/`);
  const {
    data: putData,
    error: putError,
    putData: putAction,
    loading: putLoading,
  } = usePut(siteUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
  } = useDelete(siteUrl);
  const { loading: purchaseCreateLoading, postData: purchaseCreateAction } =
    usePost(`${apiUrl}/api/purchases/`);

  const modifiedSiteTypeOptions = useMemo(
    () =>
      withIcons(getModifiedResponse(optionsData, "sitetype"), siteTypeIconMap),
    [optionsData],
  );
  const modifiedProductOptions = useMemo(
    () =>
      withIcons(getModifiedResponse(optionsData, "products"), productIconMap),
    [optionsData],
  );
  const modifiedModelsOptions = useMemo(
    () => withIcons(getModifiedResponse(optionsData, "models"), modelIconMap),
    [optionsData],
  );
  const modifiedOp = useMemo(
    () => toVisibleOptionList(optionsData?.data?.options),
    [optionsData],
  );
  const modifiedOpLicense = useMemo(
    () => toVisibleOptionList(licenseOptionData?.data?.options),
    [licenseOptionData],
  );
  const modifiedOems = useMemo(
    () => getModifiedResponse(optionsData, "oems"),
    [optionsData],
  );
  const modifiedDealers = useMemo(
    () => getModifiedResponse(dealerOptions, null),
    [dealerOptions],
  );
  const modifiedCustomers = useMemo(
    () => getModifiedResponse(customerOptions, null),
    [customerOptions],
  );

  useRefetchWhenMissing(
    defaultData?.OemNo,
    modifiedOems,
    modifiedOems.length > 0,
    optionDataRefetch,
  );
  useRefetchWhenMissing(
    defaultData?.DealerNo,
    modifiedDealers,
    !dealerOptionsLoading && dealerOptions != null,
    dealerOptionsRefetch,
  );
  useRefetchWhenMissing(
    defaultData?.CustomerNo,
    modifiedCustomers,
    !customerOptionsLoading && customerOptions != null,
    customerOptionsRefetch,
  );

  const handleCreateNew = (type) => {
    setModalType(type);
    setIsOpenModal(true);
  };

  useEffect(() => {
    if (
      defaultData?.OemNo ||
      defaultData?.DealerNo ||
      defaultData?.CustomerNo
    ) {
      form.setFieldsValue(defaultData);
    }
  }, [defaultData, form]);

  const buildSitePayload = (values = {}) => {
    const payload = {};
    const oemNo =
      values?.OemNo ??
      (hasRole(7, 8, 9) ? userData?.oem_no : null) ??
      (id ? data?.data?.OemNo : null);
    if (!isBlank(oemNo)) payload.OemNo = oemNo;

    if (values?.DeviceName != null && String(values.DeviceName).trim() !== "") {
      payload.DeviceName = values.DeviceName;
    }
    if (values?.DeviceDesc != null && String(values.DeviceDesc).trim() !== "") {
      payload.DeviceDesc = values.DeviceDesc;
    }

    const dealerNo = hasRole(7, 8, 9)
      ? (values?.DealerNo ?? userData?.dealer_no)
      : values?.DealerNo;
    const customerNo = values?.CustomerNo;
    if (id) {
      // Edit: always send DealerNo / CustomerNo (null when not selected)
      payload.DealerNo = isBlank(dealerNo) ? null : dealerNo;
      payload.CustomerNo = isBlank(customerNo) ? null : customerNo;
    } else {
      if (!isBlank(dealerNo)) payload.DealerNo = dealerNo;
      if (!isBlank(customerNo)) payload.CustomerNo = customerNo;
    }

    if (form.getFieldValue("Expired")) {
      payload.Options = applyExpiredToOptions(
        id ? data?.data?.Options : [],
        true,
      );
    }
    return payload;
  };

  const onFinish = async (values) => {
    const payload = buildSitePayload(values);
    if (isBlank(payload.OemNo)) {
      openErrorNotification("OEM is required.");
      return;
    }
    try {
      if (id) {
        await putAction(payload);
      } else {
        await postAction(payload);
      }
    } catch (error) {
      console.error("Submission error:", error);
      openErrorNotification(
        id ? "Failed to edit site." : "Failed to create site.",
      );
    }
  };

  const onReset = () => navigate(-1);

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  const onPurchaseLicenseConfirm = async () => {
    const pending = pendingPurchaseLicenseRef.current;
    pendingPurchaseLicenseRef.current = null;
    if (!pending?.OemNo || !pending?.DealerNo) {
      openErrorNotification("OEM and Dealer are required.");
      return;
    }
    if (!pending?.SiteNo) {
      openErrorNotification("Site is not available.");
      return;
    }
    if (purchaseCreateLoading) return;

    try {
      const result = await purchaseCreateAction({
        PurchaseTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        PurchaseType: 0, // On-Site
        OemNo: pending.OemNo,
        DealerNo: pending.DealerNo,
      });
      if (result != null) {
        const rid = getPurchaseRowId(result?.data ?? result);
        setTimeout(() => {
          navigate(
            rid != null
              ? `/site/${pending.SiteNo}/purchase-license/${rid}`
              : `/site/info/${pending.SiteNo}`,
          );
        }, 200);
      } else {
        openErrorNotification("Something went wrong");
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
  };

  // Defaults for the Add page
  useEffect(() => {
    setLicenseType(orderTypeOptions[0].value);
    if (!id) {
      form.setFieldsValue({
        Product: modifiedProductOptions[0]?.value,
        Model: modifiedModelsOptions[0]?.value,
        LicenseType: orderTypeOptions[0].value,
        Options: [],
        SiteType: hasRole(7, 8, 9)
          ? modifiedSiteTypeOptions[3]?.value
          : modifiedSiteTypeOptions[0]?.value,
      });
      if (hasRole(7, 8, 9)) {
        setSiteType(3);
      }
    }
  }, [optionsData]);

  useEffect(() => {
    if (!id || loading || !modifiedOp || !modifiedOpLicense) return;
    try {
      form.resetFields();
      const site = data?.data;
      if (!site) {
        console.warn("No data found for the given ID");
        return;
      }
      const initialLicenseType = licenseTypeForSiteType(site.SiteType) ?? 0;
      setLicenseType(initialLicenseType);
      form.setFieldsValue({
        DeviceName: site.DeviceName || "",
        DeviceDesc: site.DeviceDesc || "",
        OemNo: site.OemNo,
        DealerNo: site.DealerNo,
        CustomerNo: site.CustomerNo,
        SiteType: site.SiteType,
        Product: site.Product,
        Model: site.Model,
        Type: site.Type || 0,
        Mac: site.Mac || "",
        SiteKey: site.SiteKey || "",
        LicenseKey: site.LicenseKey || "",
        AddTime: toDisplayDateTime(site.AddTime),
        ActTime: toDisplayDateTime(site.ActTime),
        Options: site.Options?.map(String),
        LicenseType: initialLicenseType,
        Expired: site.Options?.includes(64) ?? false,
        FirstName: toText(site.FirstName),
        LastName: toText(site.LastName),
        Company: toText(site.Company),
        Email: toText(site.Email),
        Phone: toText(site.Phone),
        Address: toText(site.Address),
        Address2: toText(site.Address2),
        InstallType: toText(site.InstallType),
        Suggestion: toText(site.Suggestion),
        ProxyServer: site.ProxyAddr,
        SecurePort: toText(site.SecurePort),
        DebugPort: toText(site.DebugPort),
        Version: toText(site.Version),
        Https: site.Https ? "Yes" : "No",
        Timezone: toText(site.Timezone),
        Online: site.Online ? "Yes" : "No",
      });
      setLicenseOptionsData(
        buildLicenseOptionsState(site, modifiedOpLicense, modifiedOp),
      );
      setDefaultData((prev) => {
        const next = {
          OemNo: site.OemNo || null,
          DealerNo: site.DealerNo || null,
          CustomerNo: site.CustomerNo || null,
        };
        return prev.OemNo === next.OemNo &&
          prev.DealerNo === next.DealerNo &&
          prev.CustomerNo === next.CustomerNo
          ? prev
          : { ...prev, ...next };
      });
      setSiteType(site.SiteType || 0);
    } catch (error) {
      console.log({ error });
      openErrorNotification("Error fetching data!");
    }
  }, [id, data, loading, modifiedOp, modifiedOpLicense]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (postData) {
      openConfirmNotification("Success");
      const newId = getCreatedId(postData);
      navigate(newId != null ? `/site/info/${newId}` : -1);
    }
  }, [postError, postData]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (putData) {
      refetch();
      openConfirmNotification("Success");
      navigate(-1);
    }
  }, [putData, putError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate("/site");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (id && loading) return;
    const newLicenseType = licenseTypeForSiteType(
      form.getFieldValue("SiteType"),
    );
    if (newLicenseType === undefined) return;
    setLicenseType(newLicenseType);
    form.setFieldsValue({ LicenseType: newLicenseType });
  }, [sitetype, type, data]);

  useEffect(() => {
    if (data?.data?.SiteType === 3 && type === "edit") return;
    if (!form.getFieldValue("DealerNo") && !id) {
      form.setFieldsValue({ SiteType: hasRole(7, 8, 9) ? 3 : 0 });
    }
  }, [form.getFieldValue("DealerNo"), form.getFieldValue("OemNo")]);

  useEffect(() => {
    if (
      id &&
      type === "edit" &&
      [3, 6, 10, 11, 12].includes(userData?.role_value)
    ) {
      navigate("/");
    }
  }, []);

  const effectiveDealerNoForLicense =
    data?.data?.DealerNo ??
    form.getFieldValue("DealerNo") ??
    (isDealerUser ? userData?.dealer_no : null);

  const onPurchaseLicenseClick = () => {
    const oemNo =
      form.getFieldValue("OemNo") ??
      data?.data?.OemNo ??
      userData?.oem_no ??
      null;
    if (isBlank(oemNo)) {
      openErrorNotification("OEM is required.");
      return;
    }
    if (isBlank(effectiveDealerNoForLicense)) {
      openErrorNotification("Dealer is required.");
      return;
    }
    if (!id) {
      openErrorNotification("Site is not available.");
      return;
    }
    pendingPurchaseLicenseRef.current = {
      OemNo: Number(oemNo),
      DealerNo: Number(effectiveDealerNoForLicense),
      SiteNo: Number(id),
    };
    setShowPurchaseLicenseConfirm(true);
  };

  const breadcrumbItems = [
    { label: "Site", href: "/site" },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/site/info/${id}`
          : `/site/edit/${id}`
        : "/site/add",
    },
  ];

  const listButton = {
    label: "List",
    icon: <UnorderedListOutlined />,
    onClick: () => navigate(-1),
  };
  const applyButton = {
    label: "Apply",
    icon: <CheckCircleOutlined />,
    onClick: () => form.submit(),
    loading: type === "edit" ? putLoading : postLoading || putLoading,
  };
  const cancelButton = {
    label: "Cancel",
    icon: <CloseOutlined />,
    onClick: onReset,
  };

  const infoButtons = [
    ...(hasRole(7, 8)
      ? [
          {
            label: "Pre-Purchased License",
            icon: <GiftFilled />,
            onClick: () => navigate(`/site/${id}/pre-purchase-license`),
          },
        ]
      : []),
    ...(hasRole(...SITE_EDIT_ROLES) && !isBlank(effectiveDealerNoForLicense)
      ? [
          {
            label: "Purchase License",
            icon: <FaDollarSign />,
            onClick: onPurchaseLicenseClick,
          },
        ]
      : []),
    ...(hasRole(1, 2, 3, 4, 5, 6, 7, 8, 9)
      ? [{ name: "Expired", label: "Expired", toggle: true, disabled: true }]
      : []),
    ...(hasRole(...SITE_EDIT_ROLES)
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => navigate(`/site/edit/${id}`),
          },
        ]
      : []),
    ...(hasRole(1, 2)
      ? [
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => setShowModal(true),
          },
        ]
      : []),
    listButton,
  ];

  const buttonsData =
    type === "info"
      ? infoButtons
      : type === "edit"
        ? [
            {
              name: "Expired",
              label: "Expired",
              toggle: true,
              onClick: () => {},
            },
            applyButton,
            cancelButton,
          ]
        : [applyButton, cancelButton];

  const currentSiteType = form.getFieldValue("SiteType");
  const siteTypeInfoOptions =
    currentSiteType === 3 || currentSiteType === 4
      ? modifiedSiteTypeOptions.filter((item) => item.value === currentSiteType)
      : hasRole(7, 8, 9) && currentSiteType == null
        ? modifiedSiteTypeOptions.filter((item) => [3, 4].includes(item.value))
        : modifiedSiteTypeOptions.filter(
            (item) => ![3, 4].includes(item.value),
          );
  const showDeviceFields = type === "info" && [0, 1, 2].includes(sitetype);

  return (
    <div>
      <AccountCreationModal
        isOpenModal={isOpenModal}
        modalType={modalType}
        setIsOpenModal={setIsOpenModal}
        setDefaultData={setDefaultData}
        defaultData={defaultData}
        setModalType={setModalType}
        isModal={true}
        isModalForm={true}
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            {modalType === "OEM" ? (
              <OemIcon className="text-[15px] text-black ml-1" />
            ) : modalType === "Dealer" ? (
              <DealerIcon className="text-[15px] text-black ml-1" />
            ) : modalType === "Customer" ? (
              <AccountIcon className="text-[15px] text-black ml-1" />
            ) : null}
            <span className="text-[14px] font-[Poppins] text-black  ml-2">
              {isOpenModal ? `Add ${modalType}` : "Send Invitation"}
            </span>
          </div>
        }
      />
      <div className="">
        <Form
          validateTrigger={"onChange"}
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          layout={"vertical"}
          autoComplete={"off"}
        >
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>

          <FormSection icon={FaGlobe} title="Site" className="">
            <Row gutter={[16, 8]}>
              <Col {...HALF}>
                <CustomInput
                  label="Device Name"
                  name="DeviceName"
                  autoComplete="off"
                  disable={type === "info"}
                  loading={loading}
                />
              </Col>
              <Col {...HALF}>
                <CustomInput
                  label="Description"
                  name="DeviceDesc"
                  disable={type === "info"}
                  loading={loading}
                />
              </Col>
              {[1, 2, 3].includes(userData?.role_value) && (
                <Col {...HALF}>
                  <CustomSelect
                    label="OEM"
                    name="OemNo"
                    options={modifiedOems}
                    placeholder="Please select"
                    disabled={type === "info"}
                    required
                    onChange={(value) => {
                      const nextSiteType = hasRole(7, 8, 9)
                        ? data?.data?.SiteType
                        : 0;
                      setDefaultData((prev) => ({
                        ...prev,
                        OemNo: value,
                        DealerNo: null,
                        CustomerNo: null,
                      }));
                      setOemNo(value);
                      setDealerNo(null);
                      setSiteType(nextSiteType);
                      form.setFieldsValue({
                        DealerNo: null,
                        CustomerNo: null,
                        SiteType: nextSiteType,
                      });
                    }}
                    handleCreateNew={handleCreateNew}
                    type={"OEM"}
                    loading={loading}
                    allowClear={false}
                  />
                </Col>
              )}
              {[1, 2, 3, 4, 5, 6].includes(userData?.role_value) && (
                <Col {...HALF}>
                  <CustomSelect
                    label="Dealer"
                    name="DealerNo"
                    options={modifiedDealers}
                    placeholder="Please select"
                    disabled={type === "info" || !defaultData?.OemNo}
                    onChange={(value) => {
                      setDefaultData((prev) => ({
                        ...prev,
                        DealerNo: value,
                        CustomerNo: null,
                      }));
                      setDealerNo(value);
                      form.setFieldsValue({ CustomerNo: null });
                      if (!value) {
                        form.setFieldsValue({
                          SiteType: hasRole(7, 8, 9) ? data?.data?.SiteType : 0,
                        });
                      }
                    }}
                    handleCreateNew={handleCreateNew}
                    type={"Dealer"}
                    loading={loading}
                  />
                </Col>
              )}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].includes(userData?.role_value) && (
                <Col {...HALF}>
                  <CustomSelect
                    label="Customer"
                    name="CustomerNo"
                    options={modifiedCustomers}
                    placeholder="Please select"
                    disabled={
                      type === "info" ||
                      (!defaultData?.DealerNo && !hasRole(7, 8, 9))
                    }
                    handleCreateNew={handleCreateNew}
                    type={"Customer"}
                    loading={loading}
                  />
                </Col>
              )}
              {type === "info" && (
                <Col {...HALF}>
                  <CustomSelect
                    label="Site Type"
                    name="SiteType"
                    options={siteTypeInfoOptions}
                    placeholder="Please select"
                    loading={loading}
                    disabled
                    allowClear={false}
                    showIcons
                  />
                </Col>
              )}
              {showDeviceFields && (
                <Col {...HALF}>
                  <CustomSelect
                    label="Product"
                    name="Product"
                    options={modifiedProductOptions}
                    placeholder="Please select"
                    showIcons
                    disabled
                    required
                    loading={loading}
                  />
                </Col>
              )}
              {showDeviceFields && (
                <Col {...HALF}>
                  <CustomInput
                    label="MAC"
                    name="Mac"
                    disable
                    loading={loading}
                    rules={[{ validator: macValidator }]}
                    required
                  />
                </Col>
              )}
              {type === "info" && (
                <>
                  <Col {...HALF}>
                    <CustomInput
                      label="Site Key"
                      name="SiteKey"
                      disabled
                      loading={loading}
                    />
                  </Col>
                  <Col {...HALF}>
                    <CustomInput
                      label="License Key"
                      name="LicenseKey"
                      disabled
                      loading={loading}
                    />
                  </Col>
                </>
              )}
              {type === "info" && isSystemUser && (
                <>
                  <Col {...HALF}>
                    <CustomInput
                      label="Add Time"
                      name="AddTime"
                      disabled
                      loading={loading}
                    />
                  </Col>
                  <Col {...HALF}>
                    <CustomInput
                      label="Activate Time"
                      name="ActTime"
                      disabled
                      loading={loading}
                    />
                  </Col>
                </>
              )}
            </Row>
          </FormSection>

          {type === "info" && (
            <>
              <FormSection
                icon={InfoCircleOutlined}
                title="License Information"
              >
                <Row gutter={[16, 8]}>
                  <Col {...HALF}>
                    <CustomSelect
                      label="Model"
                      name="Model"
                      options={modifiedModelsOptions}
                      placeholder="Please select"
                      disabled
                      required
                      loading={loading}
                      showIcons
                    />
                  </Col>
                  <Col {...HALF}>
                    <CustomInput
                      label="Door"
                      name="Type"
                      disabled
                      type="number"
                      integerOnly
                      required
                      prefix={<DoorTypeIcon />}
                      loading={loading}
                    />
                  </Col>
                </Row>
              </FormSection>

              <FormSection
                icon={SettingOutlined}
                title="License Options"
                bodyClassName="p-3"
              >
                <OptionCards
                  name="Options"
                  options={modifiedOp}
                  disabled
                  loading={loading || codesDataLoading}
                  codesData={codesData}
                  type={licenseType}
                  value={licenseOptionsData}
                  onChange={setLicenseOptionsData}
                  hidePrices
                />
              </FormSection>

              {data?.data?.Guest ? (
                <FormSection
                  icon={InfoCircleOutlined}
                  title="Customer Information"
                  bordered={false}
                >
                  <Row gutter={[16, 16]}>
                    {renderReadOnlyFields(CUSTOMER_INFO_FIELDS)}
                  </Row>
                </FormSection>
              ) : null}

              {![3, 4].includes(currentSiteType) && isSystemUser && (
                <FormSection
                  icon={InfoCircleOutlined}
                  title="Status"
                  bordered={false}
                >
                  <Row gutter={[16, 8]}>
                    {renderReadOnlyFields(STATUS_FIELDS)}
                  </Row>
                </FormSection>
              )}
            </>
          )}
        </Form>
        {type === "info" && isSystemUser && (
          <DeviceLogsOrdersPanel deviceNo={id} logsLabel="Site Logs" />
        )}
      </div>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
      <ConfirmModal
        showModal={showPurchaseLicenseConfirm}
        setShowModal={setShowPurchaseLicenseConfirm}
        onDeleteConfirm={onPurchaseLicenseConfirm}
        content="Are you sure you want to purchase license?"
      />
    </div>
  );
};
